import { VisitDto } from '@ecdlink/core';
import { StepItem } from '@ecdlink/ui';

export const getSortedVisits = (visitsToSort: VisitDto[]) => {
  return visitsToSort.sort((a, b) => {
    if (a === undefined && b === undefined) {
      return 0;
    }
    if (a === undefined) {
      return -1;
    }
    if (b === undefined) {
      return 1;
    }

    if (a.visitType && b.visitType) {
      return (
        new Date(a.orderDate).getTime() - new Date(b.orderDate).getTime() ||
        a.visitType.order - b.visitType.order!
      );
    } else {
      return new Date(a.orderDate).getTime() - new Date(b.orderDate).getTime();
    }
  });
};

export const isVisitComplete = (visit: VisitDto): boolean => {
  if (visit) {
    return visit.attended;
  }
  return false;
};

export const isVisitLessThan3DaysOld = (visit: VisitDto): boolean => {
  if (isVisitComplete(visit) || !visit.startedDate) {
    return false;
  }

  const visitStarted = new Date(visit.startedDate);
  if (isNaN(visitStarted.getTime())) {
    return false;
  }

  const now = new Date();

  // Reset both dates to midnight (00:00:00)
  visitStarted.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);

  const diffMs = now.getTime() - visitStarted.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  return diffDays < 3;
};

export const getVisitStatus = (visit: VisitDto): StepItem['type'] => {
  const is48HoursOld = isVisitLessThan3DaysOld(visit);
  const isCompleted = isVisitComplete(visit);

  if (isCompleted || (!!visit.startedDate && !is48HoursOld)) {
    return 'completed';
  }

  if (visit.startedDate && is48HoursOld) {
    return 'inProgress';
  }

  return 'todo';
};

export const canStartVisit = (visit: VisitDto, visitType?: string): boolean => {
  if (isVisitComplete(visit)) return false;
  if (isVisitInProgress(visit)) return true;

  const todayStartMs = new Date().setHours(0, 0, 0, 0);
  const todayEndMs = new Date().setHours(23, 59, 59, 999);

  const plannedMs = new Date(visit.plannedVisitDate).getTime();

  // Cannot start future planned visits
  if (plannedMs && plannedMs >= todayEndMs) {
    return false;
  }

  if (visitType === 'mother') {
    return true;
  }

  // ── Non-mother visits ──────────────────────────────────────
  const dueMs = visit.dueDate ? new Date(visit.dueDate).getTime() : null;

  // Already past due date (previous days)
  if (dueMs && dueMs < todayStartMs) {
    return false;
  }

  // Explicitly marked missed
  if (isVisitMissed(visit)) {
    return false;
  }

  return true;
};

export const getPastVisitSubTitle = (
  visit: VisitDto,
  isAdditionalVisit: boolean
): string => {
  const visitIsComplete = isVisitComplete(visit);
  if (visitIsComplete) {
    return 'completed';
  }

  const is48HoursOld = isVisitLessThan3DaysOld(visit);
  if (!visitIsComplete && is48HoursOld) {
    return 'Visit incomplete';
  }

  return isAdditionalVisit && visit.comment!
    ? visit.comment
    : !visitIsComplete
    ? 'Missed visit'
    : '';
};

export const getVisitSubTitle = (
  visit: VisitDto,
  visitType?: string
): string => {
  const date = new Date(visit.orderDate);
  date.setHours(0, 0, 0, 0);

  const completeBy = `By ${date.getDate()} ${date.toLocaleString('default', {
    month: 'long',
  })} ${date.getFullYear()}`;

  if (isVisitLessThan3DaysOld(visit)) {
    return 'Visit incomplete';
  }

  if (isVisitInProgress(visit)) {
    return completeBy;
  }

  // Visit incomplete and expired
  if (visitType === 'mother') {
    if (!visit.attended && !!visit.startedDate) {
      return `Visit incomplete`;
    }
  } else {
    if (
      !visit.attended &&
      !!visit.startedDate &&
      new Date(visit.dueDate).getTime() < new Date().getTime()
    ) {
      return `Missed visit deadline`;
    }
  }

  if (
    visit.visitType?.normalizedName === 'Additional visits' &&
    visit.comment
  ) {
    return visit.comment;
  }

  return completeBy;
};

export const isVisitInProgress = (visit: VisitDto): boolean => {
  if (!visit.attended && !!visit.startedDate) {
    return true;
  }

  return false;
};

export const isVisitMissed = (visit: VisitDto): boolean => {
  const isComplete = isVisitComplete(visit);
  const is48HoursOld = isVisitLessThan3DaysOld(visit);

  if (!isComplete && new Date(visit.dueDate).getTime() > new Date().getTime()) {
    return false;
  }

  if (!isComplete && !!visit.startedDate && is48HoursOld) {
    return true;
  }

  return false;
};

export const getAdditionalVisitButtonVisibility = (
  visit: VisitDto
): boolean => {
  // EC-3923 - additional visits can only be started the next day
  if (visit.visitType?.name === 'additional_visits' && visit.comment !== '') {
    const insertedDate = new Date(visit.insertedDate);
    const now = new Date();

    const insertedUTC = Date.UTC(
      insertedDate.getUTCFullYear(),
      insertedDate.getUTCMonth(),
      insertedDate.getUTCDate()
    );
    const todayUTC = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate()
    );

    // Allow if today is the day AFTER insertedDate
    return todayUTC > insertedUTC;
  }

  return true;
};
